import { useCallback, useMemo, useState } from 'react';
import { hierarchyFilterRows, hierarchyRootPaths, hierarchySearchPaths } from 'src/components/star-grid/utils/hierarchyFilterRows';
import type { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';

interface Options {
  readonly members: readonly PnlHierarchyMember[];
  readonly values: readonly string[];
  readonly multiple: boolean;
  readonly onChange: (values: readonly string[]) => void;
}

/**
 * Open/expansion/search state for a hierarchical filter, plus the rows to render.
 *
 * Selection is by member ID, not by tree path: the same member can appear under several parents and
 * selecting it means the member, wherever it sits. Expansion IS by path, because two positions of the
 * same member expand independently.
 */
export const useHierarchyFilter = ({ members, values, multiple, onChange }: Options) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Roots start expanded: a panel that opens fully collapsed hides everything the filter is for.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(hierarchyRootPaths(members)));

  const searchPaths = useMemo(() => {
    const paths = hierarchySearchPaths(members, search);
    return paths.size || search.trim() ? paths : undefined;
  }, [members, search]);
  const rows = useMemo(() => hierarchyFilterRows(members, expanded, searchPaths), [members, expanded, searchPaths]);
  const selected = useMemo(() => new Set(values), [values]);

  const toggleExpanded = useCallback((path: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const toggleSelected = useCallback(
    (id: string) => {
      // Single-select applies and CLOSES: with one value there is nothing further to pick, and
      // leaving the panel open over a report the click just re-scoped hid the result of the
      // action. Multi-select deliberately stays open — the next click is usually another member.
      if (!multiple) {
        onChange([id]);
        setOpen(false);
        return;
      }
      onChange(values.includes(id) ? values.filter((value) => value !== id) : [...values, id]);
    },
    [multiple, onChange, values]
  );

  return {
    open,
    setOpen,
    search,
    setSearch,
    rows,
    selected,
    // Searching overrides expansion, so offer collapse-all only when it can do something.
    collapseAll: useCallback(() => setExpanded(new Set<string>()), []),
    expandRoots: useCallback(() => setExpanded(new Set(hierarchyRootPaths(members))), [members]),
    toggleExpanded,
    toggleSelected,
    clear: useCallback(() => onChange([]), [onChange])
  };
};
